import React, { createContext, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { CLICK_IDS } from '@/constants/CustomerLabs';

/** context for the UTM parameters */
export const UTMContext = createContext<URLSearchParams | null>(null);

export const UTMProvider = ({ children }: { children: React.ReactNode }) => {
  const [utmParams, setUtmParams] = useState<URLSearchParams | null>(null);
  const location = useLocation();
  const isClickIdKey = (key: string): key is (typeof CLICK_IDS)[number] =>
    CLICK_IDS.includes(key as (typeof CLICK_IDS)[number]);

  /** Extract UTM parameters from the current URL and store them in state */
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const trackedEntries = [...urlParams.entries()].filter(
      ([key]) => key.startsWith('utm_') || isClickIdKey(key)
    );

    if (trackedEntries.length > 0) {
      setUtmParams(new URLSearchParams(trackedEntries));
    } else {
      setUtmParams(null);
    }
  }, [location.search]);

  /** Append UTM parameters to a given URL */
  const appendUtmToUrl = (url: string) => {
    if (!utmParams) return url;

    const newUrl = new URL(url, window.location.origin);
    newUrl.search = new URLSearchParams({
      ...Object.fromEntries(newUrl.searchParams.entries()),
      ...Object.fromEntries(utmParams.entries()),
    }).toString();

    return newUrl.toString();
  };

  useEffect(() => {
    const originalPushState = window.history.pushState;
    const originalReplaceState = window.history.replaceState;

    window.history.pushState = (state, title, url) => {
      const newUrl = appendUtmToUrl(url as string);
      originalPushState.call(window.history, state, title, newUrl);
    };

    window.history.replaceState = (state, title, url) => {
      const newUrl = appendUtmToUrl(url as string);
      originalReplaceState.call(window.history, state, title, newUrl);
    };

    return () => {
      window.history.pushState = originalPushState;
      window.history.replaceState = originalReplaceState;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [utmParams]);
  return <UTMContext.Provider value={utmParams}>{children}</UTMContext.Provider>;
};
